import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { RouteContext, SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  isAdminOfCurrentOrganization,
  SessionProvider,
  TenantSwitchModal,
  useSession,
  useShell,
  useTenant,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import { BankOutlined } from '@ant-design/icons';
import { App, theme as antdTheme } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import { AppMessageBridgeHolder } from '@/utils/appBridge';
import './styles/adminPageShell.less';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import {
  AvatarDropdown,
  getApplicationTheme,
  getNavigationToken,
  NotificationIcon,
  PlatformThemeBoundary,
  Question,
  SelectLang,
  ThemeToggle,
} from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import { getMyAuthorizedResources } from '@/services/bff';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import { isPublicPath } from '@/utils/auth';
import defaultSettings from '../config/defaultSettings';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from './permissions/adapter';
import { errorConfig } from './requestErrorConfig';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';

const getSettingsWithSiderWidth = (locale?: string) => {
  const isDark =
    typeof window !== 'undefined' &&
    localStorage.getItem('app_nav_theme') === 'realDark';
  if (isDark && typeof document !== 'undefined') {
    document.documentElement.dataset.theme = 'dark';
  }
  return {
    ...defaultSettings,
    navTheme: isDark ? 'realDark' : 'light',
    title: getBrandAppName(locale),
    siderWidth: getStoredSiderWidth(),
  };
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.replace(applicationReturnTo);
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!isPublicPath(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const isAdminPath = (pathname: string) => pathname.startsWith('/admin');

const CurrentTenant: React.FC = () => {
  const { currentTenantUuid, tenants } = useTenant();
  const { currentUser } = useSession();
  const [tenantModalOpen, setTenantModalOpen] = React.useState(false);
  const currentTenant = tenants.find(
    (tenant) => tenant.id === currentTenantUuid,
  );

  if (!currentTenant) {
    return null;
  }

  // 与切换企业弹窗同一口径：一个租户可有多个组织机构，当前项按 lastLoginOrganizationUuid 匹配
  const currentOrganizationUuid = currentUser?.lastLoginOrganizationUuid;
  const currentName =
    currentTenant.organizations?.find(
      (org) => (org.orgId || org.id) === currentOrganizationUuid,
    )?.name || currentTenant.name;

  return (
    <>
      <button
        type="button"
        className="app-current-tenant"
        title={currentName}
        aria-label={`切换企业，当前：${currentName}`}
        onClick={() => setTenantModalOpen(true)}
      >
        <span className="app-current-tenant-icon" aria-hidden="true">
          <BankOutlined />
        </span>
        <span className="app-current-tenant-name">{currentName}</span>
      </button>
      <TenantSwitchModal
        open={tenantModalOpen}
        onClose={() => setTenantModalOpen(false)}
        labels={{
          title: '切换企业',
          empty: '暂无可切换企业',
          switchAction: '切换企业',
          createAction: '创建/加入企业',
          deleteAction: '删除企业',
        }}
      />
    </>
  );
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle }) => {
  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
    </div>
  );
};

const SessionSync: React.FC = () => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { currentTenantUuid } = useTenant();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const activeTenantUuid = currentTenantUuid || currentUser?.tenantUuid || '';

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      currentTenantUuid: activeTenantUuid || null,
      loading,
      featureFlags: shellConfig.featureFlags,
    });
  }, [activeTenantUuid, currentUser, loading, shellConfig.featureFlags]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = useDarkMode ? 'dark' : 'light';
    }

    setAntdConfig((prevConfig) => ({
      ...prevConfig,
      theme: {
        ...prevConfig?.theme,
        ...getApplicationTheme(useDarkMode),
      },
    }));
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    syncBrandDocument(runtimeLocale);
  }, [runtimeLocale]);

  return null;
};

export const rootContainer = (container: React.ReactNode) => {
  return <RootSessionContainer>{container}</RootSessionContainer>;
};

const RootSessionContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <SessionSync />
      {children}
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = isAdminOfCurrentOrganization(
        tenantList.find((tenant) => tenant.id === currentTenantUuid),
      );
    }
  }

  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

const LayoutContentWrapper: React.FC<{
  children: React.ReactNode;
  showNav: boolean;
  resizeEnabled: boolean;
  siderWidth: number;
  handleResizeWidth: (nextWidth: number) => void;
  initialState: any;
  setInitialState: any;
}> = ({
  children,
  showNav,
  resizeEnabled,
  siderWidth,
  handleResizeWidth,
  initialState,
  setInitialState,
}) => {
  const { hasSiderMenu } = React.useContext(RouteContext);
  const { pathname } = history.location;
  const shellClassName = isAdminPath(pathname) ? 'adminPageShell' : '';
  const { navTheme } = useTheme();

  React.useEffect(() => {
    if (initialState?.settings?.navTheme !== navTheme) {
      setInitialState((prev: any) => {
        if (!prev || prev.settings?.navTheme === navTheme) return prev;
        return {
          ...prev,
          settings: {
            ...prev.settings,
            navTheme,
          },
        };
      });
    }
  }, [navTheme, initialState?.settings?.navTheme, setInitialState]);

  return (
    <PlatformThemeBoundary>
      <div className={shellClassName}>{children}</div>
      <SiderResizeHandle
        enabled={showNav && resizeEnabled && !!hasSiderMenu}
        siderWidth={siderWidth}
        onResizeWidth={handleResizeWidth}
      />
      {isDev && (
        <SettingDrawer
          disableUrlParams
          enableDarkTheme
          settings={initialState?.settings}
          onSettingChange={(settings) => {
            setInitialState((preInitialState: any) => ({
              ...preInitialState,
              settings,
            }));
          }}
        />
      )}
    </PlatformThemeBoundary>
  );
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const isDark = initialState?.settings?.navTheme === 'realDark';
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    navTheme: isDark ? 'realDark' : 'light',
    className: 'crm-shell crm-content-theme',
    contentWidth: 'Fluid',
    breadcrumbRender: false,
    headerHeight: 44,
    splitMenus: true,
    suppressSiderWhenMenuEmpty: true,
    // splitMenus 取路径匹配到的第一个菜单项的子菜单作为侧边栏；无名的 /admin 跳转路由是所有页面的前缀，
    // 留在菜单数据里会抢先匹配且没有子菜单，导致侧边栏整体消失
    menuDataRender: (menuData) => menuData.filter((item) => item.name),
    contentStyle: {
      height: 'calc(100dvh - 44px)',
      overflow: 'auto',
      minWidth: 0,
      padding: 0,
      backgroundColor: isDark
        ? 'var(--app-content-background, #121212)'
        : 'var(--app-content-background, #ffffff)',
    },
    token: {
      ...(initialState?.settings as any)?.token,
      ...getNavigationToken(isDark),
    },
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <CurrentTenant key="current-tenant" />,
        <NotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav || !dom) {
        return false;
      }
      return dom;
    },
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (isPublicPath(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => (
      <LayoutContentWrapper
        showNav={getCurrentLayoutVisibility().showNav}
        resizeEnabled={resizeEnabled}
        siderWidth={siderWidth}
        handleResizeWidth={handleResizeWidth}
        initialState={initialState}
        setInitialState={setInitialState}
      >
        {children}
      </LayoutContentWrapper>
    ),
  };
};
